import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, GraduationCap } from "lucide-react";
import { useLearnerCurriculumProgress } from "@/hooks/useLearnerCurriculumProgress";
import { CurriculumProgressSummaryCard } from "@/components/lms/CurriculumProgressSummaryCard";

/**
 * Dashboard widget: one progress block per enrolled programme.
 *
 * Renders nothing when the learner has no enrolments, so the dashboard is not
 * padded with an empty card for staff or brand-new users.
 */
export function LearningProgressWidget() {
  const { data: progress, isLoading } = useLearnerCurriculumProgress();

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Your learning</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </CardContent>
      </Card>
    );
  }

  if (!progress || progress.length === 0) return null;

  // Least-finished first: the programme needing attention leads.
  const ordered = [...progress].sort((a, b) => {
    const aPct = a.isCohortGated ? a.assignedPercent : a.overallPercent;
    const bPct = b.isCohortGated ? b.assignedPercent : b.overallPercent;
    return aPct - bPct;
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <GraduationCap className="h-4 w-4" />
            Your learning
          </CardTitle>
          <Button asChild variant="ghost" size="sm">
            <Link to="/curricula?tab=enrolled">
              View all
              <ArrowRight className="h-3 w-3 ml-1" />
            </Link>
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {ordered.map((summary) => (
          <div key={summary.curriculumId}>
            <Link
              to={`/curricula/${summary.curriculumId}`}
              className="block hover:opacity-80 transition-opacity"
            >
              <CurriculumProgressSummaryCard summary={summary} />
            </Link>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
